/**
 * Single source of truth for board layout: row grouping, ordering, and row heights.
 * Used by BoardViewPage and PublicBoardPage so both render the same layout.
 */

export const BASE_ROW_HEIGHT_PX = 260;
export const MIN_ROW_H = 0.1;
export const MAX_ROW_H = 6;

/**
 * Normalize card positions on load: assign rowIndex when missing, output canonical { rowIndex, order, w, h }.
 * All cards in the same row share the same h (row-level height).
 */
export function normalizeCardsOnLoad(cards) {
  if (!cards || !Array.isArray(cards) || cards.length === 0) return cards;
  const hasRowIndex = (c) => typeof c?.position?.rowIndex === "number";
  const withRow = cards.filter(hasRowIndex);
  const withoutRow = cards.filter((c) => !hasRowIndex(c));
  const maxRow = withRow.length > 0 ? Math.max(...withRow.map((c) => c.position.rowIndex), -1) : -1;
  const assigned = withoutRow.map((card, i) => ({
    ...card,
    position: { rowIndex: maxRow + 1 + i, order: 0, w: 1, h: 1 },
  }));
  const all = [...withRow, ...assigned];
  const groups = new Map();
  all.forEach((card) => {
    const r = card.position?.rowIndex ?? 0;
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(card);
  });
  const rows = Array.from(groups.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, row]) => row.slice().sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0)));
  const cardIdToPosition = new Map();
  rows.forEach((row, rowIndex) => {
    const rowHeight = row.length > 0 && typeof row[0]?.position?.h === "number" ? row[0].position.h : 1;
    if (row.length === 1) {
      cardIdToPosition.set(row[0]?.id, { rowIndex, order: 0, w: 1, h: rowHeight });
      return;
    }
    row.forEach((c, order) => {
      const w = typeof c?.position?.w === "number" ? c.position.w : 1 / row.length;
      cardIdToPosition.set(c?.id, { rowIndex, order, w, h: rowHeight });
    });
  });
  return cards.map((card) => {
    const pos = cardIdToPosition.get(card?.id);
    if (!pos) return card;
    return { ...card, position: pos };
  });
}

/**
 * Build ordered rows from normalized cards (same grouping as BoardViewPage).
 */
export function computeOrderedRows(normalizedCards) {
  const groups = new Map();
  normalizedCards.forEach((card, index) => {
    const raw =
      typeof card.position?.rowIndex === "number"
        ? card.position.rowIndex
        : typeof card.position?.rowIndex === "string"
          ? parseInt(card.position.rowIndex, 10)
          : card.position?.order ?? index;
    const rowKey = Number.isFinite(raw) ? raw : index;
    if (!groups.has(rowKey)) groups.set(rowKey, []);
    groups.get(rowKey).push(card);
  });
  return Array.from(groups.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, row]) => row.slice().sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0)));
}

/**
 * Build row height map: key = rowKey (e.g. "id1-id2"), value = h multiplier.
 */
export function computeRowHeights(orderedRows) {
  const map = {};
  orderedRows.forEach((row) => {
    if (!row || row.length === 0) return;
    const rowKey = row.map((c) => c.id).join("-");
    const h =
      typeof row[0]?.position?.h === "number" && Number.isFinite(row[0].position.h)
        ? Math.min(MAX_ROW_H, Math.max(MIN_ROW_H, row[0].position.h))
        : 1;
    map[rowKey] = h;
  });
  return map;
}
